import { create } from 'zustand';
import { ProfessionalForm } from '../containers/LME/helpers';
import { PacienteLme, PrescricaoCidLme, SMRForm } from '../containers/prescription/service';
import { UnitsPerMonth } from '../interfaces/medicine';

export interface SMRContextProps {
  smrForm: SMRForm | null;
  setSmrForm(smrForm: SMRForm | null): void;
  isPatientDataComplete: boolean;
  setIsPatientDataComplete(isComplete: boolean): void;
  isProfessionalDataComplete: boolean;
  setIsProfessionalDataComplete(isComplete: boolean): void;
  isPrescriptionDataComplete: boolean;
  setIsPrescriptionDataComplete(isComplete: boolean): void;
  updatePrescriptionIcdSmr(prescriptionIcdSmr: number, payload: Partial<PrescricaoCidLme>): void;
  updatePatientDataForm(payload: Partial<PacienteLme>): void;
  updateProfessionalDataForm(payload: ProfessionalForm): void;
  updatePrescriptionIcdSmrMedicineQuantity(
    prescriptionIcdSmrId: number,
    prescriptionMedicineId: number,
    quantities: Array<UnitsPerMonth>
  ): void;
  resetSteps(): void;
}

export const useSmrStore = create<SMRContextProps>()((set, get) => ({
  smrForm: null,
  isPatientDataComplete: false,
  isProfessionalDataComplete: false,
  isPrescriptionDataComplete: false,
  setSmrForm: (data: SMRForm | null) => {
    set((prev) => {
      return {
        ...prev,
        smrForm: data,
      };
    });
  },
  setIsPatientDataComplete: (isComplete: boolean) => {
    set((prev) => {
      return {
        ...prev,
        isPatientDataComplete: isComplete,
      };
    });
  },
  setIsProfessionalDataComplete: (isComplete: boolean) => {
    set((prev) => {
      return {
        ...prev,
        isProfessionalDataComplete: isComplete,
      };
    });
  },
  setIsPrescriptionDataComplete: (isComplete: boolean) => {
    set((prev) => {
      return {
        ...prev,
        isPrescriptionDataComplete: isComplete,
      };
    });
  },
  updatePrescriptionIcdSmr: (prescriptionIcdSmr: number, payload: Partial<PrescricaoCidLme>) => {
    set((prev) => {
      if (!prev || !prev.smrForm) return prev;

      const prescriptionIcdSmrInState = prev.smrForm.prescricaoCidLme.find(
        (item) => item.idPrescricaoCidLme === prescriptionIcdSmr
      );

      if (prescriptionIcdSmrInState) {
        Object.keys(payload).forEach((key) => {
          // @ts-ignore
          prescriptionIcdSmrInState[key] = payload[key];
        });
      }

      return {
        ...prev,
        smrForm: {
          ...prev.smrForm,
          prescricaoCidLme: prev.smrForm.prescricaoCidLme,
        },
      };
    });
  },
  updatePatientDataForm: (payload: Partial<PacienteLme>) => {
    set((prev) => {
      if (!prev || !prev.smrForm) return prev;

      return {
        ...prev,
        smrForm: {
          ...prev.smrForm,
          pacienteLme: {
            ...prev.smrForm.pacienteLme,
            ...payload,
          },
        },
      };
    });
  },
  updateProfessionalDataForm: (payload: ProfessionalForm) => {
    set((prev) => {
      if (!prev || !prev.smrForm) return prev;

      return {
        ...prev,
        smrForm: {
          ...prev.smrForm,
          medicoLme: {
            ...prev.smrForm.medicoLme,
            cns: payload.doctorCNS,
            nome: payload.doctorName,
          },
          estabelecimentoLme: {
            ...prev.smrForm.estabelecimentoLme,
            cnes: payload.CNESNumber,
            nome: payload.establishment,
          },
        },
      };
    });
  },
  updatePrescriptionIcdSmrMedicineQuantity: (
    prescriptionIcdSmrId: number,
    prescriptionMedicineId: number,
    quantities: Array<UnitsPerMonth>
  ) => {
    set((prev) => {
      if (!prev || !prev.smrForm) return prev;

      const prescriptionIcdInState = prev.smrForm.prescricaoCidLme.find(
        (item) => item.idPrescricaoCidLme === prescriptionIcdSmrId
      );

      if (!prescriptionIcdInState) return prev;

      const medicineInPrescriptionIcd = prescriptionIcdInState.medicamentosLme.find(
        (item) => item.idPrescricaoMedicamento === prescriptionMedicineId
      );

      if (!medicineInPrescriptionIcd) return prev;

      const updatedMedicine = {
        ...medicineInPrescriptionIcd,
        MedicamentosUnidadeLme: quantities,
      };

      return {
        ...prev,
        smrForm: {
          ...prev.smrForm,
          prescricaoCidLme: prev.smrForm.prescricaoCidLme.map((prescriptionIcdSmr) => {
            if (prescriptionIcdSmr.idPrescricaoCidLme !== prescriptionIcdSmrId) return prescriptionIcdSmr;

            return {
              ...prescriptionIcdSmr,
              medicamentosLme: prescriptionIcdSmr.medicamentosLme.map((medicineSmr) => {
                if (medicineSmr.idPrescricaoMedicamento !== prescriptionMedicineId) return medicineSmr;

                return updatedMedicine;
              }),
            };
          }),
        },
      };
    });
  },
  resetSteps: () => {
    set((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        isPatientDataComplete: false,
        isPrescriptionDataComplete: false,
        isProfessionalDataComplete: false,
      };
    });
  },
}));
